import React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { objectToQuery, requestApi } from "../../../helpers/RequestApi";
import { GET } from "../../../utils/MethodUtils";
import { FDOCENTETES_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../helpers/GetErrorApi";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { FDocenteteInterface } from "../../../interfaces/FDocenteteInterface";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Divider from "@mui/material/Divider";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}, &.${tableCellClasses.footer}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const CheckInvoiceAlreadyExistsComponent: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const [loading, setLoading] = React.useState(false);
  const [fDocentetes, setFDocentetes] = React.useState<FDocenteteInterface[]>(
    []
  );

  const searchFDocentetes = () => {
    (async () => {
      if (search.trim().length < 3) {
        return;
      }
      setFDocentetes([]);
      setLoading(true);
      const response = await requestApi({
        method: GET,
        path:
          FDOCENTETES_URL +
          objectToQuery({
            doType: 12,
            search: search.trim(),
          }),
        allowError: false,
        paginate: true,
        token: token,
      });
      if (response.statusCode === 200) {
        setFDocentetes(response.content.fDocentetes);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    })();
  };

  React.useEffect(() => {
    const timeoutTyping = setTimeout(() => {
      searchFDocentetes();
    }, 500);
    return () => clearTimeout(timeoutTyping);
  }, [search.trim()]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <TextField
        autoComplete="off"
        sx={{ width: "100%" }}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        label={t("word.invoiceAlreadyDone")}
        InputProps={{
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
      />
      {!loading && search.trim().length >= 3 && fDocentetes.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t("column.doPiece")}</StyledTableCell>
                  <StyledTableCell>{t("word.supplier")}</StyledTableCell>
                  <StyledTableCell>{t("word.doRef")}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fDocentetes.map((fDocentete, indexFDocentete) => (
                  <StyledTableRow key={indexFDocentete}>
                    <StyledTableCell>{fDocentete.doPiece}</StyledTableCell>
                    <StyledTableCell>
                      {fDocentete.doTiers?.ctNum}
                    </StyledTableCell>
                    <StyledTableCell>{fDocentete.doRef}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ marginBottom: 4 }} />
        </>
      )}
    </>
  );
});

export default CheckInvoiceAlreadyExistsComponent;
